<template>
  <div class="select-project-grouped">
    <!-- Поиск -->
    <div class="search-section">
      <q-input
        v-model="searchText"
        outlined
        dense
        placeholder="Поиск по ЖК или типу проекта..."
        clearable
        autofocus
        class="search-input"
      >
        <template v-slot:prepend>
          <q-icon name="search" />
        </template>
      </q-input>
    </div>

    <q-linear-progress v-if="loading" indeterminate />

    <!-- Дерево ЖК -->
    <div v-else class="tree-section">
      <div v-if="groupedProjects.length === 0" class="empty-state">
        <q-icon name="folder_open" size="64px" color="grey-5" />
        <div class="empty-text">Проекты не найдены</div>
      </div>

      <div v-else class="tree-container">
        <div 
          v-for="resident in filteredProjects" 
          :key="resident.resident_id" 
          class="resident-group"
        >
          <!-- Заголовок ЖК -->
          <div 
            class="resident-header"
            :class="{ 'resident-header--expanded': expandedResidents[resident.resident_id] }"
            @click="toggleResident(resident.resident_id)"
          >
            <q-icon 
              :name="expandedResidents[resident.resident_id] ? 'expand_more' : 'chevron_right'" 
              size="20px"
            />
            <q-icon name="apartment" size="20px" color="primary" class="q-mr-sm" />
            <span class="resident-name">{{ resident.resident_name }}</span>
            <q-badge color="grey-6" class="q-ml-sm">
              {{ resident.project_types.length }}
            </q-badge>
          </div>

          <!-- Типы проектов внутри ЖК -->
          <div v-if="expandedResidents[resident.resident_id]" class="project-types">
            <div 
              v-for="projectType in resident.project_types" 
              :key="`${resident.resident_id}-${projectType.project_type_id}`"
              class="project-type-item"
              :class="{ 
                'project-type-item--selected': isSelected(resident.resident_id, projectType.project_type_id) 
              }"
              @click="selectProjectType(resident, projectType)"
            >
              <q-icon name="construction" size="18px" color="orange" class="q-mr-sm" />
              <span class="project-type-name">{{ projectType.project_type_name }}</span>
              <q-badge color="blue-grey-4" class="q-ml-auto">
                {{ projectType.project_count }} проектов
              </q-badge>
              <q-icon 
                v-if="isSelected(resident.resident_id, projectType.project_type_id)"
                name="check_circle" 
                size="20px" 
                color="positive"
                class="q-ml-sm"
              />
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Кнопки -->
    <div class="actions-section">
      <q-btn flat label="Сбросить" color="grey-7" @click="clearSelection" />
      <q-btn flat label="Отмена" color="grey-7" @click="closeModal" />
      <q-btn 
        color="primary" 
        label="Выбрать" 
        @click="confirmSelection"
        :disable="!selectedItem"
      />
    </div>
  </div>
</template>

<script>
import { useAppStore } from "@/services/store/app";
import { supplierRequestsAPI } from "@/views/SupplierRequests/services/api";

export default {
  name: "select-project-grouped",
  props: {
    data: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      loading: false,
      searchText: "",
      groupedProjects: [],
      expandedResidents: {},
      selectedItem: null
    };
  },
  computed: {
    filteredProjects() {
      if (!this.searchText) {
        return this.groupedProjects;
      }
      
      const query = this.searchText.toLowerCase();
      return this.groupedProjects.filter(resident => {
        const residentMatch = resident.resident_name?.toLowerCase().includes(query);
        const typeMatch = resident.project_types.some(pt => 
          pt.project_type_name?.toLowerCase().includes(query)
        );
        return residentMatch || typeMatch;
      }).map(resident => {
        if (resident.resident_name?.toLowerCase().includes(query)) {
          return resident;
        }
        return {
          ...resident,
          project_types: resident.project_types.filter(pt =>
            pt.project_type_name?.toLowerCase().includes(query)
          )
        };
      });
    }
  },
  methods: {
    async loadProjects() {
      this.loading = true;
      try {
        const res = await supplierRequestsAPI.getProjectsGrouped();
        const data = res?.data || [];
        
        // Группируем по ЖК
        const grouped = {};
        data.forEach(item => {
          if (!grouped[item.resident_id]) {
            grouped[item.resident_id] = {
              resident_id: item.resident_id,
              resident_name: item.resident_name,
              project_types: []
            };
          }
          grouped[item.resident_id].project_types.push({
            project_type_id: item.project_type_id,
            project_type_name: item.project_type_name,
            project_count: item.project_count,
            project_ids: item.project_ids
          });
        });
        
        this.groupedProjects = Object.values(grouped).sort((a, b) => 
          a.resident_name?.localeCompare(b.resident_name)
        );
        
        // Предустановим выбранное значение если передано
        if (this.data?.resident_id && this.data?.project_type_id) {
          const resident = this.groupedProjects.find(r => r.resident_id === this.data.resident_id);
          if (resident) {
            const projectType = resident.project_types.find(pt => pt.project_type_id === this.data.project_type_id);
            if (projectType) {
              this.selectedItem = {
                resident_id: resident.resident_id,
                resident_name: resident.resident_name,
                project_type_id: projectType.project_type_id,
                project_type_name: projectType.project_type_name
              };
              this.expandedResidents[resident.resident_id] = true;
            }
          }
        }
      } catch (e) {
        console.error('Error loading projects:', e);
      } finally {
        this.loading = false;
      }
    },
    toggleResident(residentId) {
      this.expandedResidents[residentId] = !this.expandedResidents[residentId];
    },
    isSelected(residentId, projectTypeId) {
      return this.selectedItem?.resident_id === residentId && 
             this.selectedItem?.project_type_id === projectTypeId;
    },
    selectProjectType(resident, projectType) {
      this.selectedItem = {
        resident_id: resident.resident_id,
        resident_name: resident.resident_name,
        project_type_id: projectType.project_type_id,
        project_type_name: projectType.project_type_name
      };
    },
    clearSelection() {
      this.selectedItem = null;
    },
    confirmSelection() {
      if (this.data?.onSelect) {
        this.data.onSelect(this.selectedItem);
      }
      this.closeModal();
    }
  },
  mounted() {
    this.loadProjects();
  },
  setup() {
    const { closeModal } = useAppStore();
    return { closeModal };
  }
};
</script>

<style scoped lang="scss">
.select-project-grouped {
  width: 600px;
  max-width: 90vw;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  padding: 20px;
}

.search-section {
  margin-bottom: 16px;
}

.search-input {
  width: 100%;
}

.tree-section {
  flex: 1;
  overflow-y: auto;
  min-height: 300px;
  max-height: 400px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  background: #fafafa;
}

.tree-container {
  padding: 8px;
}

.resident-group {
  margin-bottom: 4px;
}

.resident-header {
  display: flex;
  align-items: center;
  padding: 10px 12px;
  background: white;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
  border: 1px solid transparent;
  
  &:hover {
    background: #e3f2fd;
    border-color: #1976d2;
  }
  
  &--expanded {
    background: #e8f5e9;
    border-color: #4caf50;
  }
}

.resident-name {
  font-weight: 600;
  font-size: 14px;
  color: #333;
}

.project-types {
  padding-left: 24px;
  margin-top: 4px;
}

.project-type-item {
  display: flex;
  align-items: center;
  padding: 8px 12px;
  margin: 2px 0;
  background: white;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s ease;
  border: 1px solid #e0e0e0;
  
  &:hover {
    background: #fff3e0;
    border-color: #ff9800;
  }
  
  &--selected {
    background: #e8f5e9;
    border-color: #4caf50;
  }
}

.project-type-name {
  font-size: 13px;
  color: #555;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
  color: #9e9e9e;
}

.empty-text {
  margin-top: 12px;
  font-size: 14px;
}

.actions-section {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid #e0e0e0;
}
</style>

